import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClipCard, Eyebrow } from "@/components/media/Sections";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check } from "lucide-react";

export const JourneyFeed = ({ journey, clips, categories }) => {
  const [filter, setFilter] = useState("ALL");
  const used = useMemo(
    () => categories.filter((c) => clips.some((cl) => cl.category === c)),
    [categories, clips]
  );
  const shown = filter === "ALL" ? clips : clips.filter((c) => c.category === filter);

  if (!clips.length) return null;

  return (
    <>
      <Eyebrow>STRAIGHT FROM THE TENT</Eyebrow>
      <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">{journey.title}</h2>
      <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">{journey.intro}</p>

      {used.length > 1 ? (
        <div className="-mx-5 mt-8 flex gap-2 overflow-x-auto px-5 pb-2 md:mx-0 md:flex-wrap md:px-0">
          {["ALL", ...used].map((c) => (
            <button
              key={c}
              data-testid={`journey-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`mono shrink-0 border px-3 py-2 text-[10px] tracking-widest transition-colors ${
                filter === c
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:border-primary/60 hover:text-primary"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {shown.map((clip) => (
          <ClipCard key={clip.id} clip={clip} />
        ))}
      </div>
    </>
  );
};

export const AcademyModules = ({ pitch }) => (
  <>
    <Eyebrow>WHAT SMASH ACADEMY TEACHES</Eyebrow>
    <h2 className="neon mt-3 max-w-4xl text-4xl leading-tight sm:text-5xl">{pitch.headline}</h2>
    <div className="mt-6 max-w-2xl space-y-4 text-base text-muted-foreground md:text-lg">
      {(pitch.body || "").split("\n\n").map((p) => (
        <p key={p.slice(0, 24)}>{p}</p>
      ))}
    </div>

    <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {(pitch.modules || []).map((m, i) => (
        <div
          key={m.id}
          data-testid={`academy-module-${m.id}`}
          className="border border-border bg-[#141414] p-5 transition-colors hover:border-primary/60"
        >
          <span className="mono text-[9px] tracking-widest text-muted-foreground">
            {String(i + 1).padStart(2, "0")}
          </span>
          <h3 className="display mt-2 text-2xl uppercase leading-none text-primary">{m.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.body}</p>
        </div>
      ))}
    </div>

    <Link
      to={pitch.cta_href || "/academy"}
      data-testid="academy-pitch-cta"
      className="display mt-12 inline-flex rounded-sm bg-primary px-7 py-4 text-xl uppercase text-primary-foreground transition-colors hover:bg-[#E6C800]"
    >
      {pitch.cta}
    </Link>
  </>
);

export const Realities = ({ realities }) => (
  <>
    <Eyebrow>RADICAL TRANSPARENCY</Eyebrow>
    <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">{realities.title}</h2>
    <ul className="mt-10 grid gap-x-10 gap-y-3 sm:grid-cols-2">
      {(realities.items || []).map((item) => (
        <li
          key={item}
          className="flex items-start gap-3 border-b border-border/60 pb-3 text-base text-muted-foreground"
        >
          <span className="mono mt-1 shrink-0 text-primary">—</span>
          {item}
        </li>
      ))}
    </ul>
    <p className="mt-10 max-w-3xl text-base text-white md:text-lg">{realities.closing}</p>
    <p
      data-testid="realities-pull-quote"
      className="display mt-12 border-l-4 border-primary pl-5 text-4xl uppercase leading-none text-primary sm:text-5xl lg:text-6xl"
    >
      {realities.pull_quote}
    </p>
  </>
);

const money = (n) => {
  const v = Number.isFinite(n) ? n : 0;
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

const FIELDS = [
  ["cups", "CUPS SOLD"],
  ["price", "AVERAGE SELLING PRICE ($)"],
  ["ingredient", "INGREDIENT COST PER CUP ($)"],
  ["fee", "EVENT FEE ($)"],
  ["labor", "LABOR ($)"],
  ["travel", "TRAVEL ($)"],
];

export const EventEconomics = ({ economics }) => {
  const [form, setForm] = useState(() =>
    Object.fromEntries(FIELDS.map(([k]) => [k, String(economics.defaults?.[k] ?? 0)]))
  );
  const n = (k) => parseFloat(form[k]) || 0;

  const revenue = n("cups") * n("price");
  const ingredients = n("cups") * n("ingredient");
  const expenses = ingredients + n("fee") + n("labor") + n("travel");
  const profit = revenue - expenses;
  const margin = n("price") - n("ingredient");
  const breakEven = margin > 0 ? Math.ceil((n("fee") + n("labor") + n("travel")) / margin) : 0;

  return (
    <>
      <Eyebrow>NO GUARANTEES, JUST THE MATH</Eyebrow>
      <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">{economics.title}</h2>
      <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">{economics.intro}</p>

      <div className="mono mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] tracking-widest text-muted-foreground">
        <span className="text-white">CUPS SOLD</span><span className="text-primary">×</span>
        <span className="text-white">AVG PRICE</span><span className="text-primary">=</span>
        <span className="text-primary">REVENUE</span><span className="text-primary">−</span>
        <span>INGREDIENTS</span><span className="text-primary">−</span>
        <span>EVENT FEE</span><span className="text-primary">−</span>
        <span>LABOR</span><span className="text-primary">−</span>
        <span>TRAVEL</span><span className="text-primary">=</span>
        <span className="text-primary">EVENT PROFIT</span>
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 border border-border bg-[#141414] p-6 md:p-8">
          {FIELDS.map(([key, label]) => (
            <div key={key}>
              <Label className="mono text-[10px] tracking-widest text-muted-foreground">
                {label}
              </Label>
              <Input
                data-testid={`econ-input-${key}`}
                type="number"
                step="0.01"
                inputMode="decimal"
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="mt-1 rounded-sm bg-[#0A0A0A]"
              />
            </div>
          ))}
        </div>

        <div data-testid="econ-results" className="border border-primary/50 bg-[#191603] p-6 md:p-8">
          {[
            ["Revenue", money(revenue), false],
            ["Ingredients", money(-ingredients), false],
            ["Event fee", money(-n("fee")), false],
            ["Labor", money(-n("labor")), false],
            ["Travel", money(-n("travel")), false],
          ].map(([label, value]) => (
            <div
              key={label}
              className="flex items-baseline justify-between gap-4 border-b border-border/60 py-3"
            >
              <span className="text-sm text-muted-foreground">{label}</span>
              <span className="display text-2xl text-muted-foreground">{value}</span>
            </div>
          ))}
          <div className="flex items-baseline justify-between gap-4 py-4">
            <span className="text-base text-white">Event profit</span>
            <span
              data-testid="econ-profit"
              className={`display text-4xl ${profit >= 0 ? "text-primary" : "text-destructive"}`}
            >
              {money(profit)}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-4 border-t border-border/60 py-3">
            <span className="text-sm text-muted-foreground">Break-even cups</span>
            <span data-testid="econ-breakeven" className="display text-2xl text-white">
              {breakEven.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <p
        data-testid="econ-disclaimer"
        className="mono mt-8 max-w-3xl border-l-2 border-border pl-4 text-[11px] leading-relaxed text-muted-foreground"
      >
        {economics.disclaimer}
      </p>
    </>
  );
};

export const OperatorPaths = ({ paths, disclaimer }) => (
  <>
    <Eyebrow>TWO STARTING POINTS, NOT A LADDER</Eyebrow>
    <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">Choose how you want to start.</h2>
    <div className="mt-12 grid gap-6 lg:grid-cols-2">
      {paths.map((p) => (
        <div
          key={p.key}
          data-testid={`path-${p.key}`}
          className="flex flex-col border border-border bg-[#141414] p-6 transition-colors hover:border-primary/60 md:p-8"
        >
          <h3 className="display text-3xl uppercase leading-none md:text-4xl">{p.name}</h3>
          <div className="display mt-3 text-5xl leading-none text-primary">{p.price_label}</div>
          <p className="mt-4 text-base text-white">{p.headline}</p>
          <div className="mono mt-6 text-[10px] tracking-widest text-muted-foreground">
            WHAT'S INCLUDED
          </div>
          <ul className="mt-2 flex-1 space-y-1.5">
            {(p.features || []).map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                <Check size={14} className="mt-0.5 shrink-0 text-primary" />
                {f}
              </li>
            ))}
          </ul>
          <Link
            to={p.cta_destination || "/apply"}
            data-testid={`path-cta-${p.key}`}
            className="display mt-8 inline-flex w-full items-center justify-center rounded-sm border border-primary px-5 py-3 text-lg uppercase text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            {p.cta_text || "SEE IF YOU'RE A FIT"}
          </Link>
        </div>
      ))}
    </div>
    {disclaimer ? (
      <p
        data-testid="paths-disclaimer"
        className="mono mt-10 max-w-3xl border-l-2 border-border pl-4 text-[11px] leading-relaxed text-muted-foreground"
      >
        {disclaimer}
      </p>
    ) : null}
  </>
);

export const Community = ({ community }) => (
  <>
    <Eyebrow>COMMUNITY BEFORE TRANSACTION</Eyebrow>
    <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">{community.title}</h2>
    <p className="mt-5 max-w-3xl text-lg text-white md:text-2xl">{community.body}</p>
    <div className="mt-10 flex flex-wrap gap-3">
      {(community.groups || []).map((g) => (
        <span
          key={g}
          className="mono border border-border px-4 py-2 text-[10px] tracking-widest text-muted-foreground"
        >
          {g.toUpperCase()}
        </span>
      ))}
    </div>
  </>
);

export const Faq = ({ faq }) => (
  <>
    <Eyebrow>STRAIGHT ANSWERS</Eyebrow>
    <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">Questions we actually get.</h2>
    <div className="mt-10 divide-y divide-border border-y border-border">
      {(faq || []).map((item) => (
        <details key={item.id} data-testid={`faq-${item.id}`} className="group py-5">
          <summary className="display cursor-pointer list-none text-2xl uppercase leading-tight text-white transition-colors hover:text-primary md:text-3xl">
            {item.q}
          </summary>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground md:text-base">
            {item.a}
          </p>
        </details>
      ))}
    </div>
  </>
);
